/** A field in a widget's settings schema — rendered generically by the inspector. */

/**
 * Central registry. Built-in widgets register here at boot; third parties extend
 * it via `app.bespoke.widgets.add(...)`. The editor's widget tray and the inspector
 * form are both generated from this — no bespoke UI per widget.
 */
export default class WidgetRegistry {
  defs = new Map();
  add(def) {
    this.defs.set(def.type, def);
    return this;
  }

  /**
   * Drain definitions pushed onto the global `window.BespokeWidgetQueue` —
   * the order-proof registration path for extensions that can't (or don't
   * want to) import Bespoke directly. Mirrors BespokePanelQueue: queue at any
   * time, we absorb on every lookup.
   */
  drainQueue() {
    const queue = window.BespokeWidgetQueue;
    if (Array.isArray(queue) && queue.length) {
      queue.splice(0).forEach(def => {
        if (def && def.type && def.component && !this.defs.has(def.type)) this.add(def);
      });
    }
  }
  get(type) {
    this.drainQueue();
    return this.defs.get(type);
  }
  all() {
    this.drainQueue();
    return [...this.defs.values()];
  }
  forZone(zone) {
    return this.all().filter(d => d.zones.includes(zone));
  }

  /** Resolve an instance's settings against its schema defaults. */
  resolve(instance) {
    const def = this.get(instance.type);
    if (!def) return {};
    const out = {};
    for (const f of def.schema) out[f.key] = instance.settings?.[f.key] ?? f.default;
    return out;
  }
}